/**
 * Spoken Hindi announcements while dealing, using the browser's speech synthesis.
 * Uses a Hindi voice with Devanagari text when the device has one, otherwise the
 * same words in Roman Hindi with an Indian-English (or default) voice.
 */
import { useSyncExternalStore } from 'react';
import { currentDealer, matchStatus, rangPlayerOfDeal, totals, trumpCallerSeat, turnInfo, type DealRecord, type MatchState, type Suit } from '../game/match';
import type { SeatPlayer, Teams } from '../shared/schemas';
import { KEYS, load, save } from './storage';

export interface Phrase {
  hi: string;
  roman: string;
  /** Voice tweaks for a playful line (defaults: pitch 1, rate 0.95). */
  pitch?: number;
  rate?: number;
}

/** A silent gap between lines. */
export interface Pause {
  pauseMs: number;
}

/** A pre-recorded clip to play (e.g. the "7 Guaa" milestone sting), instead of synthesised speech. */
export interface Sound {
  src: string;
}

export type Speech = Phrase | Pause | Sound;

/** How many times the Guaa cheer is repeated, and the quiet gap before play goes on. */
export const GUAA_REPEAT = 2;
export const GUAA_PAUSE_MS = 5000;

/** A team reaching this many Guaa plays a special clip instead of the usual cheer. */
export const SEVEN_GUAA_MILESTONE = 7;
export const SEVEN_GUAA_CLIP = '/audio/saath-karod.mp3';
export const SEVEN_GUAA_PAUSE_MS = 4000;

/** Quiet gap after "… bhaag gaya!" (online game) before the rest is announced. */
export const RUN_PAUSE_MS = 3000;

const ORDINAL: [string, string][] = [
  ['पहला', 'pehla'],
  ['दूसरा', 'doosra'],
  ['तीसरा', 'teesra'],
  ['चौथा', 'chautha'],
  ['पाँचवाँ', 'paanchvaan'],
  ['छठा', 'chhatha'],
];
const COUNT: [string, string][] = [
  ['शून्य', 'shoonya'],
  ['एक', 'ek'],
  ['दो', 'do'],
  ['तीन', 'teen'],
  ['चार', 'chaar'],
  ['पाँच', 'paanch'],
  ['छह', 'chhe'],
  ['सात', 'saat'],
  ['आठ', 'aath'],
  ['नौ', 'nau'],
  ['दस', 'das'],
  ['ग्यारह', 'gyaarah'],
  ['बारह', 'baarah'],
];
/** Hindi number words up to 12; larger numbers are read out as digits. */
const count = (n: number): [string, string] => COUNT[n] ?? [String(n), String(n)];

/** "Ashu pehla haath baant raha hai" / "… ka aakhri haath hai baantne ka". */
export function handPhrase(name: string, hand: number, of: number, to?: string): Phrase {
  // "Reyan Ashu ko …": who receives the cards (the player choosing Rang), when known.
  const toHi = to ? `${to} को ` : '';
  const toRoman = to ? `${to} ko ` : '';
  if (of <= 1) return { hi: `${name} ${toHi}पत्ते बाँट रहा है`, roman: `${name} ${toRoman}patte baant raha hai` };
  if (hand >= of) {
    return to
      ? { hi: `${name} का आख़िरी हाथ है, ${to} को बाँट रहा है`, roman: `${name} ka aakhri haath hai, ${to} ko baant raha hai` }
      : { hi: `${name} का आख़िरी हाथ है बाँटने का`, roman: `${name} ka aakhri haath hai baantne ka` };
  }
  const [hi, roman] = ORDINAL[hand - 1] ?? [`${hand}वाँ`, `${hand}vaan`];
  return { hi: `${name} ${toHi}${hi} हाथ बाँट रहा है`, roman: `${name} ${toRoman}${roman} haath baant raha hai` };
}

/**
 * "Guaa" stretched out to "Guaaah" when spoken — a few extra आ at the end draws
 * the vowel out instead of clipping it short. Edit the spelling here:
 *   hi    — Devanagari, read by a Hindi voice (most phones)
 *   roman — used when the phone has no Hindi voice
 */
const GUAA_WORD: Phrase = { hi: 'गुआआआ', roman: 'guaaah' };

/**
 * The cheer after "… ne guaa khaya". Edit the spelling here:
 *   hi    — Devanagari, read by a Hindi voice (most phones)
 *   roman — used when the phone has no Hindi voice
 *   pitch / rate — higher and faster than normal speech, for fun
 */
export const GUAA_CHANT: Phrase = { hi: 'शुलुल्लुलु… शुलुल्लूऊऊलु!', roman: 'shulullulu… shululluuuulu!', pitch: 1.6, rate: 1.15 };

/** "Ashu ne guaaah khaya — shulullulu shulullulu!" (a court gets a mention). */
export function guaaPhrases(name: string, court = false): Phrase[] {
  return [
    court
      ? { hi: `${name} ने कोर्ट के साथ ${GUAA_WORD.hi} खाया`, roman: `${name} ne court ke saath ${GUAA_WORD.roman} khaaya` }
      : { hi: `${name} ने ${GUAA_WORD.hi} खाया`, roman: `${name} ne ${GUAA_WORD.roman} khaaya` },
    GUAA_CHANT,
  ];
}

/** "Reyan ne ek guaaah khaya hai… shulullulu shulullulu!" — a player's running tally. */
export function guaaTallyPhrases(name: string, guaa: number): Phrase[] {
  if (guaa === 0) return [{ hi: `${name} ने एक भी ${GUAA_WORD.hi} नहीं खाया`, roman: `${name} ne ek bhi ${GUAA_WORD.roman} nahi khaaya` }];
  const [hi, roman] = count(guaa);
  return [
    { hi: `${name} ने ${hi} ${GUAA_WORD.hi} खाया है`, roman: `${name} ne ${roman} ${GUAA_WORD.roman} khaaya hai` },
    GUAA_CHANT,
  ];
}

/** What to say right after a deal is saved: the Guaa cheer, if any (said once; see `afterDealSpeech`). */
export function resultPhrases(deal: DealRecord, state: MatchState, seats: SeatPlayer[], teams: Teams): Phrase[] {
  if (deal.result !== 'guaa') return [];
  const seat = rangPlayerOfDeal(deal, state.rules);
  const name = seat !== null ? seats[seat]!.name : deal.trumpTeam ? teams[deal.trumpTeam].name : 'Rang team';
  return guaaPhrases(name, deal.isCourt);
}

/** "Ashu ke teen haath khatam hue, ab Zakir patte baantega". */
export function turnEndPhrase(name: string, hands: number, next: string): Phrase {
  const [hi, roman] = count(hands);
  return {
    hi: `${name} के ${hi} हाथ ख़त्म हुए, अब ${next} पत्ते बाँटेगा`,
    roman: `${name} ke ${roman} haath khatam hue, ab ${next} patte baantega`,
  };
}

// ---------------------------------------------------------------------------
// On/off preference, and which voice to use
// ---------------------------------------------------------------------------

let enabled = load<boolean>(KEYS.voice, true);
// A device can list several voices tagged hi-*, and mispronunciation reports
// have come from the app guessing wrong between them (and from the "best"
// guess changing after a browser/OS update, with no way for us to test every

// device). Rather than keep guessing, the chosen voice is picked once —
// remembered by `voiceKey()` — so it's stable until the person picks a
// different one. Some phones list two voices that both report the same
// `.name` (e.g. two "Hindi India" entries, one for Devanagari and one for
// Latin-script text); matching by name alone silently confused the two, so
// the key includes `lang` as well, which is where they actually differ.
let chosenVoiceKey = load<string | null>(KEYS.voiceName, null);
const listeners = new Set<() => void>();

/** A stable identity for a voice: its name alone is not always unique (see above). */
export const voiceKey = (v: SpeechSynthesisVoice) => `${v.name}|${v.lang}`;

export const voiceSupported = () => typeof window !== 'undefined' && 'speechSynthesis' in window;

export function setVoiceEnabled(on: boolean) {
  enabled = on;
  save(KEYS.voice, on);
  if (!on) stopSpeaking();
  listeners.forEach((l) => l());
}

export function useVoiceEnabled(): boolean {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => enabled,
  );
}

/** Remembers which voice to use (its `voiceKey()`), or `null` to go back to auto-pick. */
export function setChosenVoice(key: string | null) {
  chosenVoiceKey = key;
  save(KEYS.voiceName, key);
  listeners.forEach((l) => l());
}

export function useChosenVoiceKey(): string | null {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => chosenVoiceKey,
  );
}

// ---------------------------------------------------------------------------
// Speaking
// ---------------------------------------------------------------------------

/** Every Hindi-tagged voice this device offers (there can be more than one). */
export function listHindiVoices(): SpeechSynthesisVoice[] {
  if (!voiceSupported()) return [];
  return speechSynthesis.getVoices().filter((v) => v.lang.toLowerCase().startsWith('hi'));
}

/**
 * True for a voice tagged to read Latin-script text (BCP-47 `#Latn`/`-Latn`
 * script subtag) rather than Devanagari — some Android phones list one of
 * each under the same "Hindi India" display name. Feeding Devanagari text to
 * the Latin-script voice is a plausible way to get a badly mangled reading
 * even though the device does have a correct Hindi voice available too.
 */
const isLatinScriptVoice = (v: SpeechSynthesisVoice) => /[-_#]latn\b/i.test(v.lang);

/**
 * The voice to speak with. If the person has picked one (Match options → Voice
 * announcements), that's used until they change it — we can't tell from here
 * which of a device's Hindi voices actually sounds right, and auto-picking has
 * gone wrong before (including going backwards when we tried to be clever
 * about it), so a direct choice is the only reliable fix. Auto-pick is just a
 * reasonable starting point: the Devanagari `hi-IN` region tag (never the
 * Latin-script variant) over a bare `hi-*`.
 */
function pickVoice(): SpeechSynthesisVoice | null {
  const candidates = listHindiVoices();
  if (candidates.length === 0) return null;
  if (chosenVoiceKey) {
    const picked = candidates.find((v) => voiceKey(v) === chosenVoiceKey);
    if (picked) return picked;
  }
  // Android sometimes reports the region with an underscore ("hi_IN"); normalise before comparing.
  const devanagari = candidates.filter((v) => !isLatinScriptVoice(v));
  const pool = devanagari.length > 0 ? devanagari : candidates;
  return pool.find((v) => v.lang.toLowerCase().replace('_', '-') === 'hi-in') ?? pool[0]!;
}

/** The voice `pickVoice()` would currently use, for display in the picker. */
export function currentVoice(): SpeechSynthesisVoice | null {
  return pickVoice();
}

/**
 * On some Android/Chrome versions, `getVoices()` returns an empty list the
 * first time it's called — the real list only exists after `voiceschanged`
 * fires, moments later. Calling `pickVoice()` before that happened used to
 * silently fall back to Roman text with no voice picked at all (a different,
 * uncontrolled code path — the OS then chooses whatever it likes for a bare
 * "en-IN" tag, which is a plausible way to get a badly mispronounced word
 * even on a phone that does have a correct Hindi voice installed). This
 * resolves once real voices exist, or after a short timeout if the device
 * truly has none, and every caller waits on the same promise.
 */
let voicesReady: Promise<void> | null = null;
function waitForVoices(): Promise<void> {
  if (!voiceSupported()) return Promise.resolve();
  if (listHindiVoices().length > 0) return Promise.resolve();
  if (voicesReady) return voicesReady;
  voicesReady = new Promise((resolve) => {
    const done = () => {
      speechSynthesis.removeEventListener?.('voiceschanged', done);
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(done, 1200); // devices with no Hindi voice would otherwise hang
    speechSynthesis.addEventListener?.('voiceschanged', done);
    speechSynthesis.getVoices(); // some engines only start loading once asked
  });
  return voicesReady;
}
if (voiceSupported()) void waitForVoices();

/** Bumped on every announce so an older sequence (and its pause timers) stops. */
let generation = 0;

// Whether an announcement is audible right now (not during its quiet pauses) — table
// voice chat mutes the mic meanwhile, so other phones don't hear it echoed back.
let audible = false;
const audibleListeners = new Set<() => void>();
function setAudible(next: boolean) {
  if (audible === next) return;
  audible = next;
  audibleListeners.forEach((l) => l());
}
export const isAnnouncing = () => audible;
export function useAnnouncing(): boolean {
  return useSyncExternalStore(
    (cb) => {
      audibleListeners.add(cb);
      return () => void audibleListeners.delete(cb);
    },
    () => audible,
  );
}

/**
 * Speaks the lines in order, one after another, with any pauses between them,
 * replacing anything still being said. Call it from a tap handler so mobile
 * browsers allow audio. `force` speaks even when voice is switched off.
 */
export async function announce(items: Speech[], { force = false, pauseMic = true } = {}) {
  if (!voiceSupported() || (!enabled && !force) || items.length === 0) return;
  const gen = ++generation;
  await waitForVoices();
  if (gen !== generation) return; // superseded while we were waiting
  speechSynthesis.cancel();
  const hindi = pickVoice();

  const step = (i: number) => {
    if (gen !== generation) return;
    if (i >= items.length) return setAudible(false);
    const item = items[i]!;
    if ('pauseMs' in item) {
      setAudible(false);
      setTimeout(() => step(i + 1), item.pauseMs);
      return;
    }
    setAudible(pauseMic);
    if ('src' in item) {
      const audio = new Audio(item.src);
      let done = false;
      const next = () => {
        if (done) return;
        done = true;
        clearTimeout(guard);
        step(i + 1);
      };
      // A clip that fails to load (bad path, blocked autoplay) shouldn't stall the rest of the announcement.
      const guard = setTimeout(next, 8000);
      audio.onended = next;
      audio.onerror = next;
      void audio.play().catch(next);
      return;
    }
    const u = new SpeechSynthesisUtterance(hindi ? item.hi : item.roman);
    if (hindi) u.voice = hindi;
    u.lang = hindi ? hindi.lang : 'en-IN';
    u.rate = item.rate ?? 0.95;
    u.pitch = item.pitch ?? 1;
    // Some engines occasionally skip `end`; never let the sequence stall.
    let done = false;
    const next = () => {
      if (done) return;
      done = true;
      clearTimeout(guard);
      step(i + 1);
    };
    const guard = setTimeout(next, 2500 + u.text.length * 150);
    u.onend = next;
    u.onerror = next;
    speechSynthesis.speak(u);
  };
  step(0);
}

/** Stops speaking (including a pending pause). */
export function stopSpeaking() {
  generation++;
  setAudible(false);
  if (voiceSupported()) speechSynthesis.cancel();
}

// ---------------------------------------------------------------------------
// What to say for a match
// ---------------------------------------------------------------------------

/**
 * Announcement for the hand about to be dealt. When a dealer's turn has just
 * ended, it first says that and who deals next. `withTurnEnd: false` for replays.
 */
export function dealingPhrases(state: MatchState, seats: SeatPlayer[], { withTurnEnd = true } = {}): Phrase[] {
  if (matchStatus(state).phase === 'finished') return [];
  const dealer = currentDealer(state);
  if (dealer === null) return [];
  const nextNo = state.deals.length + 1;
  const { dealInTurn, dealsPerTurn } = turnInfo(state, nextNo);
  const name = seats[dealer]!.name;
  const out: Phrase[] = [];
  const last = state.deals.at(-1);
  if (withTurnEnd && last && dealInTurn === 1 && last.dealerSeat !== dealer) {
    out.push(turnEndPhrase(seats[last.dealerSeat]!.name, turnInfo(state, last.dealNo).dealsPerTurn, name));
  }
  const caller = trumpCallerSeat(dealer, state.rules);
  // When the dealer is also the Rang chooser, there's no one else to name.
  const to = caller !== null && caller !== dealer ? seats[caller]!.name : undefined;
  out.push(handPhrase(name, dealInTurn, dealsPerTurn, to));
  return out;
}

/**
 * True if the deal just recorded pushed either team's Guaa from below the
 * milestone up to it (or past it, e.g. a Court bonus jumping several at once)
 * — so it fires exactly once per team, the moment they reach it, not on
 * every deal afterwards.
 */
function justReachedMilestone(state: MatchState): boolean {
  if (state.deals.length === 0) return false;
  const before = totals(state.deals.slice(0, -1));
  const after = totals(state.deals);
  const crossed = (a: number, b: number) => a < SEVEN_GUAA_MILESTONE && b >= SEVEN_GUAA_MILESTONE;
  return crossed(before.guaaA, after.guaaA) || crossed(before.guaaB, after.guaaB);
}

/**
 * Everything said after a deal is saved. The first deal to take a team to 7
 * Guaa plays the milestone clip, then a pause, before anything else. With
 * Guaa: the cheer twice, a 5-second pause, then who deals next. Otherwise
 * just the next hand.
 */
/** What the table calls each suit. */
export const SUIT_HINDI: Record<Suit, Phrase> = {
  D: { hi: 'ईंट', roman: 'eent' },
  H: { hi: 'पान', roman: 'paan' },
  S: { hi: 'हुकुम', roman: 'hukum' },
  C: { hi: 'चिड़िया', roman: 'chidiya' },
};

/** "Rang eent hai" — said on every phone once the caller picks the suit. */
export function rangPhrase(suit: Suit): Phrase {
  return { hi: `रंग ${SUIT_HINDI[suit].hi} है`, roman: `rang ${SUIT_HINDI[suit].roman} hai` };
}

/** "Aapki baari hai" — a nudge on the phone of the player whose turn it is. */
export const YOUR_TURN: Phrase = { hi: 'आपकी बारी है', roman: 'aapki baari hai' };

/** Bandh Rang lines: Rang chosen face down, Rang opened, the run stopped, and the game over. */
export const RANG_HIDDEN: Phrase = { hi: 'रंग बंद है', roman: 'rang band hai' };
export const RANG_OPENED: Phrase = { hi: 'रंग खुल गया!', roman: 'rang khul gaya!' };
export const RUN_STOPPED: Phrase = { hi: 'भागने से रोक दिया!', roman: 'bhaagne se rok diya!' };
export const gameOverPhrase = (loser: string): Phrase => ({ hi: `खेल ख़त्म — ${loser} हार गए`, roman: `khel khatam — ${loser} haar gaye` });

/** "Thanseer bhaag gaya!" — a player ran (won two tricks in a row), deciding the hand. */
export function ranPhrase(name: string): Phrase {
  return { hi: `${name} भाग गया!`, roman: `${name} bhaag gaya!`, pitch: 1.15, rate: 1 };
}

/**
 * Online game, after a hand is decided: when someone ran, first "<name> bhaag gaya!"
 * and a 3-second pause, then the usual after-deal announcement (Guaa cheer, who deals next).
 */
export function onlineResultSpeech(state: MatchState, seats: SeatPlayer[], teams: Teams, ranBy: string | null): Speech[] {
  const rest = afterDealSpeech(state, seats, teams);
  if (!ranBy) return rest;
  return rest.length ? [ranPhrase(ranBy), { pauseMs: RUN_PAUSE_MS }, ...rest] : [ranPhrase(ranBy)];
}

export function afterDealSpeech(state: MatchState, seats: SeatPlayer[], teams: Teams): Speech[] {
  const last = state.deals.at(-1);
  const cheer = last ? resultPhrases(last, state, seats, teams) : [];
  const dealing = dealingPhrases(state, seats);
  const rest = (() => {
    if (cheer.length === 0) return dealing;
    const repeated = Array.from({ length: GUAA_REPEAT }, () => cheer).flat();
    return dealing.length ? [...repeated, { pauseMs: GUAA_PAUSE_MS }, ...dealing] : repeated;
  })();
  if (!justReachedMilestone(state)) return rest;
  const milestone: Speech[] = [{ src: SEVEN_GUAA_CLIP }, { pauseMs: SEVEN_GUAA_PAUSE_MS }];
  return [...milestone, ...rest];
}
